import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IconFont } from '../iconfont'
import { Breadcrumb, Button, Divider, Form, Modal, Select, Tree } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { OptionType } from './widgets/search-panel'
import Scrollbars from 'react-custom-scrollbars'
import { SignTitle } from '../sign-title'
import { FilterFormSelect } from '@/components/filter-form-select'
import TablePanel from './widgets/table-panel'
import { t } from '@/i18n'

const { useForm } = Form

interface PropsType {
  scene: string // ES '终端安全' CS '容器安全' TS '流量安全' WS 'Web安全' WG '威胁情报' MS '邮件安全' DP '数据防泄漏' PM '钓鱼邮件'
  taskName: string // 任务名称
  taskStatus: number // 任务状态  1 等待中 2 进行中 3 已完成 4 中断 7 停止中
  canImport?: boolean // 是否可导入离线日志
  isImporting?: boolean // 是否正在导入离线日志
  taskContent?: React.ReactNode // 收起时显示的任务内容插槽
  taskContentExpanded?: React.ReactNode // 展开时显示的任务内容插槽
  taskCharts?: React.ReactNode // 任务图表插槽
  treeData: DataNode[] // 标签树形控件数据
  filterParams: any // 筛选条件
  filterOptions: OptionType[] // 筛选条件下拉框选项
  filterFormValue: any // 筛选框值
  setFilterFormValue: (value: any) => void // 筛选框值更新的回调
  columns: any // 表格列配置
  dataSource: any[] // 表格数据
  total: number // 表格数据总数
  rowSelection: any // 表格行选择配置
  selectedRowKeys: string[] // 表格已选行
  fixedCount: number //已修正的条数
  fixedOptions: any[] // 修正选项
  fixedLoading: boolean // 修正选项加载状态
  encode?: number // 编码Payload
  loading: boolean // 表格加载状态
  scroll: any // 表格滚动配置
  openRecordModal?: (params: boolean) => void // 导入记录弹窗 visible
  openImportModal?: (params: boolean) => void // 导入离线日志弹窗 visible
  openReportModal: (params: { visible: boolean; name: string; result_id?: string }) => void // 生成报表弹窗 visible
  openDeleteModal: (params: boolean) => void // 删除弹窗 visible
  handleExpand: () => void // 展开任务内容的回调
  setFilterParams: (params: any) => void // 筛选条件更新的回调
  setSelectedRowKeys: (keys: any) => void // 表格行选择更新的回调
  handleFixed: (values: { interceptStatus: string }) => void // 修正选项更新的回调
  getEchartsData: () => void // 获取echarts数据
  removeEncode?: (checked: boolean) => void // 去除编码Payload的回调
  refreshRequest: () => void // 刷新页面请求
  handleBack: () => void // 返回上一页的回调
  fixedModalVisible?: boolean
  setFixedModalVisible?: (visible: boolean) => void
  onTableChange: (pagination: any, filters: any, sorter: any) => void // 表格变化回调
}

const DetailTemplate: React.FC<PropsType> = ({
  scene,
  taskName,
  taskStatus,
  canImport,
  isImporting,
  taskContent,
  taskContentExpanded,
  taskCharts,
  treeData,
  filterOptions, // 用例名称必须放在第一个
  filterFormValue,
  setFilterFormValue,
  filterParams,
  columns,
  dataSource,
  total,
  selectedRowKeys,
  rowSelection,
  fixedCount,
  fixedOptions,
  fixedLoading,
  scroll,
  encode,
  loading,
  openRecordModal,
  openImportModal,
  openReportModal,
  openDeleteModal,
  handleExpand,
  setFilterParams,
  setSelectedRowKeys,
  handleFixed,
  getEchartsData,
  refreshRequest,
  handleBack,
  fixedModalVisible,
  setFixedModalVisible,
  onTableChange,
}) => {
  const [form] = useForm()
  const importableList = ['ES', 'TS', 'WS', 'MS', 'ES_device', 'WG', 'CS']

  // 控制任务信息展开状态
  const [isExpand, setIsExpand] = useState(false)
  const [activeKey, setActiveKey] = useState('1')
  // 控制侧边栏收缩状态
  const [retractSider, setRetractSider] = useState(false)

  const batchOperation = (
    <Flex>
      <div className="batch">
        <span>
          {t('批量操作（已修正')}
          <span className="count">{fixedCount || 0}</span>
          {t('项）')}
        </span>
        <Divider type="vertical" />
        <div className="fix-btn" onClick={() => setFixedModalVisible?.(true)}>
          <IconFont type={t('批量修正')} />
          <span className="btn-text">{t('批量修正')}</span>
        </div>
      </div>
      <IconFont
        type="icon-guanbi"
        className="close-icon"
        onClick={() => {
          setSelectedRowKeys([])
        }}
      />
    </Flex>
  )

  // 刷新页面
  const refreshPage = () => {
    if (activeKey !== '1') {
      getEchartsData()
      refreshRequest()
    } else {
      refreshRequest()
    }
  }

  // 标签树形控件标题渲染函数
  const renderTitle = nodeData => (
    <div className="tree-title">
      {nodeData.title}
      <span style={{ marginLeft: '10px' }}>({nodeData.extra})</span>
    </div>
  )

  // 标签树形控件选择事件处理函数
  const onCheck = (checkedKeys, e) => {
    const paramKeys = [
      'tree_resource_level',
      'tree_risk_level',
      'tree_intercept_status',
      'tree_device_ids',
      'resourceLevel',
      'riskLevel',
      'status',
      'deviceIds',
      'resource_level',
    ]

    // 处理每个参数
    const newFilterParams = { ...filterParams, page: 1 }

    paramKeys.forEach(key => {
      // 过滤出以当前key开头的值，并提取实际需要的值
      const filteredKeys = checkedKeys
        .filter(checkedKey => checkedKey.startsWith(`${key}_`))
        .map(checkedKey => checkedKey.replace(`${key}_`, ''))
      console.log('filteredKeys', filteredKeys)
      // 只有当有值时才设置参数
      if (filteredKeys.length > 0) {
        newFilterParams[key] = filteredKeys
      } else {
        delete newFilterParams[key]
      }
    })

    setFilterParams(newFilterParams)
  }

  // 查询弹窗关闭事件处理函数
  const onCancel = () => {
    form.resetFields()
    setFixedModalVisible?.(false)
  }

  useEffect(() => {
    if (fixedModalVisible) {
      form.setFieldsValue({
        interceptStatus: undefined,
      })
    }
  }, [fixedModalVisible])

  useEffect(() => {
    if (treeData.length > 0) return setRetractSider(false)
    setRetractSider(true)
  }, [treeData])

  useEffect(() => {
    if (activeKey === '2') {
      getEchartsData()
    }
  }, [encode])

  return (
    <Wrapper isExpand={isExpand} retractSider={retractSider}>
      <div className="top">
        <div className="top-left">
          <div className="return" onClick={handleBack}>
            <IconFont type="icon-fanhui2" className="icon-return" />
          </div>
          <Breadcrumb>{t('任务详情')}</Breadcrumb>
        </div>
        <div className="operate-panel">
          {([2, 7].includes(taskStatus) || isImporting) && (
            <div className="refresh-btn" onClick={refreshPage}>
              <IconFont type="icon-shuaxin" className="icon-refresh" />
            </div>
          )}
          {importableList.includes(scene) && [3, 4].includes(taskStatus) && (
            <>
              <span className="record-btn" onClick={() => openRecordModal && openRecordModal(true)}>
                {t('导入记录')}
              </span>
              <Button
                type="primary"
                loading={isImporting}
                onClick={() => openImportModal && openImportModal(true)}
                disabled={isImporting ? false : scene === 'ES_device' ? true : !canImport}
              >
                {t('导入离线日志')}
              </Button>
            </>
          )}
          {![2, 7].includes(taskStatus) && (
            <Button
              type="primary"
              onClick={() => openReportModal({ visible: true, name: taskName })}
            >
              {t('生成报表')}
            </Button>
          )}
          {![2, 7].includes(taskStatus) && (
            <Button onClick={() => openDeleteModal(true)}>{t('删除')}</Button>
          )}
        </div>
      </div>
      <div className="task-panel">
        <div className="task-name">
          <span>{t('任务名称：')}</span>
          <span>{taskName}</span>
        </div>
        <div style={{ display: isExpand ? 'block' : 'none' }}>{taskContentExpanded}</div>
        <div style={{ display: !isExpand ? 'block' : 'none' }}>{taskContent}</div>
        <div className="trapezoid" onClick={() => setIsExpand(!isExpand)}>
          <div className="retract-btn">
            <span>{t('收起')}</span>
            <IconFont type="icon-xiala1" className="icon-retract" />
          </div>
          <div className="expand-btn" onClick={handleExpand}>
            <span>{t('展开')}</span>
            <IconFont type="icon-xiala1" className="icon-expend" />
          </div>
        </div>
      </div>
      <div className="use-case-panel">
        <div className="header">
          <div className="tabs">
            <Button
              className={activeKey === '1' ? 'active default' : 'default'}
              onClick={() => {
                setActiveKey('1')
                setFilterParams({
                  page: 1,
                  limit: 20,
                })
              }}
            >
              {t('用例列表')}
            </Button>
            <Button
              className={activeKey === '2' ? 'active default' : 'default'}
              onClick={() => {
                if (activeKey === '2') return
                setActiveKey('2')
                getEchartsData()
              }}
            >
              {t('任务分析')}
            </Button>
          </div>
        </div>
        {activeKey === '1' && (
          <div className="body">
            <div className="sider">
              {!retractSider && (
                <div className="top">
                  <span className="sub-title">{t('筛选列表')}</span>
                </div>
              )}
              <div
                className={retractSider ? 'expand-btn' : 'retract-btn'}
                onClick={() => {
                  setRetractSider(!retractSider)
                }}
              >
                <IconFont
                  type="icon-icongengduo"
                  className={retractSider ? 'icon-expend' : 'icon-retract'}
                />
              </div>
              {!retractSider && (
                <div className="bottom">
                  <Tree checkable onCheck={onCheck} titleRender={renderTitle} treeData={treeData} />
                </div>
              )}
            </div>
            <div className="content">
              <div className="search-bar">
                <div
                  className="search"
                  style={{ marginBottom: Object.keys(filterParams).length > 2 ? '10px' : '0' }}
                >
                  <div className="left">
                    <FilterFormSelect
                      filterOptions={filterOptions}
                      value={filterFormValue}
                      onChange={newValue => {
                        setFilterFormValue(newValue)
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="use-case-list">
                <Scrollbars>
                  <TablePanel
                    columns={columns}
                    dataSource={dataSource}
                    rowSelection={rowSelection}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    scroll={scroll}
                    page={filterParams.page || 1}
                    pageSize={filterParams.limit || 10}
                    total={total}
                    batchOperation={batchOperation}
                    onChange={onTableChange}
                  />
                </Scrollbars>
              </div>
            </div>
          </div>
        )}
        {activeKey === '2' && (
          <div className="body" style={{ padding: '20px' }}>
            {taskCharts}
          </div>
        )}
      </div>
      <FixModal
        title={<SignTitle show>{t('验证结果')}</SignTitle>}
        open={fixedModalVisible}
        onCancel={onCancel}
        centered
        loading={fixedLoading}
        onOk={() => {
          form.submit()
          setFixedModalVisible?.(false)
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleFixed}>
          <Form.Item
            name={'interceptStatus'}
            label={t('验证结果')}
            rules={[{ required: true }, { message: t('请选择验证结果') }]}
          >
            <Select placeholder={t('请选择')} style={{ width: '452px' }} options={fixedOptions} />
          </Form.Item>
        </Form>
      </FixModal>
    </Wrapper>
  )
}

export default DetailTemplate

interface WrapperProps {
  isExpand: boolean
}

const Wrapper = styled.div<WrapperProps>`
  min-height: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: column;

  .ant-space {
    gap: 0 !important;

    &.space-btn .ant-space-item {
      margin-right: 8px !important;
    }
  }

  .ant-table-column-sorter {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-table-column-sorters {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: unset;
    }

    .ant-table-filter-trigger:hover {
      background: transparent;
    }
  }

  .ant-table-measure-row {
    display: none;
  }

  .action-btn {
    display: inline-block;
    min-width: 32px;
    max-width: 48px;
    cursor: pointer;
    color: #4238c9;
    flex-shrink: 0;
  }

  .ant-table-column-sorters {
    justify-content: flex-start;
    .ant-table-column-sorter {
      padding-top: 4px;
      margin-left: 5px;
    }
    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;
    .ant-table-column-title {
      flex: none;
    }
  }

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;

    .top-left {
      display: flex;

      .return {
        width: 24px;
        height: 24px;
        background: #ffffff;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        margin-right: 12px;

        &:hover {
          background: #e6e9f0;
        }

        .icon-return {
          color: #666;
        }

        &:hover {
          cursor: pointer;
          box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);

          .icon-return {
            color: #4238c9;
          }
        }
      }

      .ant-breadcrumb {
        line-height: 24px;
      }
    }

    .operate-panel {
      display: flex;
      align-items: center;

      .refresh-btn {
        width: 24px;
        height: 24px;
        background: #ffffff;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        margin-right: 12px;
        border: 1px solid #d9d9d9;

        .icon-refresh {
          color: #666;
        }

        &:hover {
          cursor: pointer;
          background: #e6e9f0;
          box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);

          .icon-refresh {
            color: #4238c9;
          }
        }
      }
      .record-btn {
        margin-right: 16px;
        color: #4238c9;

        &:hover {
          cursor: pointer;
        }
      }
      .ant-btn:not(:last-child) {
        margin-right: 8px;
      }
    }
  }

  .task-panel {
    min-height: ${({ isExpand }) => (isExpand ? '400px' : '78px')};
    max-height: 387px;
    height: ${({ isExpand }) => (isExpand ? '400px' : '78px')};
    background: #ffffff;
    box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
    border-radius: 8px;
    padding: 16px;
    position: relative;
    transition: height 0.3s ease-in-out;
    display: flex;
    flex-direction: column;

    .task-name {
      font-weight: 500;
      font-size: 16px;
      margin-bottom: 16px;
    }

    .trapezoid {
      width: 130px; /* 梯形的宽度 */
      height: 18px; /* 梯形的高度 */
      background-color: #e7e9f3; /* 背景颜色 */
      clip-path: polygon(0 0, 35px 100%, calc(100% - 35px) 100%, 100% 0); /* 梯形的剪切路径 */
      color: #4238c9; /* 文字颜色 */
      display: flex; /* 使文本居中 */
      align-items: center; /* 垂直居中 */
      justify-content: center; /* 水平居中 */
      text-align: center; /* 文本居中 */
      position: absolute; /* 绝对定位 */
      bottom: -18px; /* 底部对齐 */
      left: 50%; /* 左边对齐 */
      transform: translateX(-50%); /* 左边对齐 */
      cursor: pointer; /* 鼠标样式 */

      .expand-btn {
        display: ${({ isExpand }) => (!isExpand ? 'block' : 'none')};
      }

      .retract-btn {
        display: ${({ isExpand }) => (isExpand ? 'block' : 'none')};
      }

      .icon-expend,
      .icon-retract {
        font-size: 16px;
        margin-left: 8px;
      }

      .icon-retract {
        transform: rotate(180deg);
      }
    }
  }

  .use-case-panel {
    margin-top: 26px;
    // max-height: 630px;
    // height: 630px;
    background: #ffffff;
    box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    flex: 1;

    .header {
      min-height: 52px;
      border-bottom: 1px solid #d5d9dd;
      padding: 0px 16px 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .tabs {
        margin-top: 16px;
      }

      .default {
        width: 120px;
        height: 36px;
        background: #fafafa;
        border-radius: 6px 6px 0px 0px;
        border: 1px solid #d9d9d9;
        border-bottom: none;
        margin-right: 4px;
        color: #666;

        &.active {
          background: #e2e4f6;
          border: 1px solid #869add;
          color: #4238c9;
          font-weight: 500;
        }
      }
    }

    .body {
      flex: 1;
      height: 580px;
      display: flex;

      .sider {
        width: ${({ retractSider }) => (!retractSider ? '290px' : '21px')};
        height: 100%;
        border-right: 1px solid #d5d9dd;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        position: relative;
        transition: width 0.3s ease-in-out;

        .top {
          display: flex;
          padding-left: 20px;
          padding-right: 16px;
          border-bottom: 1px solid #d5d9dd;
          display: flex;
          justify-content: space-between;

          span.sub-title {
            font-size: 16px;
            flex-shrink: 0;
          }

          .select-tag {
            width: 120px;
            height: 32px;
          }
        }

        .expand-btn {
          width: 24px;
          height: 24px;
          background: #ffffff;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          position: absolute;
          right: -12px;
          top: 16px;

          &:hover {
            cursor: pointer;
            border-color: #4238c9;
            box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);
          }

          .icon-expend {
            color: #666;
          }

          .icon-expend:hover {
            color: #4238c9;
          }
        }

        .retract-btn {
          width: 24px;
          height: 24px;
          background: #ffffff;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          position: absolute;
          right: -12px;
          top: 16px;

          &:hover {
            cursor: pointer;
            border-color: #4238c9;
            box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);
          }

          .icon-retract {
            color: #666;
            transform: rotate(180deg);
          }

          .icon-retract:hover {
            color: #4238c9;
          }
        }

        .bottom {
          padding-top: 15px;
          padding-left: 16px;

          .ant-tree .ant-tree-node-content-wrapper.ant-tree-node-selected {
            background-color: #ebeef7;
          }

          .ant-tree-node-content-wrapper {
            width: 180px;

            &:hover {
              background-color: #ebeef7;
            }
          }
        }
      }

      .content {
        width: ${({ retractSider }) => (!retractSider ? 'calc(100% - 290px)' : '100%')};
        display: flex;
        overflow-y: scroll;
        flex-direction: column;
        transition: width 0.3s ease-in-out;
      }

      .search-bar {
        min-height: 52px;
        max-height: 120px;
        border-bottom: 1px dashed #d5d9dd;
        display: flex;
        flex-direction: column;
        padding: 11px 14px;

        .search {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .left {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            width: 100%;
            .ant-input {
              width: 500px;
              margin-right: 8px;
            }
          }

          .toggle-btn {
            width: 70px;
            height: 32px;
            background: #eeeeee;
            border-radius: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 4px;

            .ant-btn {
              border: none;
              border-radius: 4px;
              width: 30px;
              height: 26px;

              &.active {
                background: #eee;
                color: #333;
                cursor: not-allowed;
              }
            }
          }
        }

        .search-content {
          display: flex;
          align-items: center;
          width: 100%;
          max-height: 35px;

          .filter-item {
            flex-shrink: 0;
            max-width: 160px;
            display: inline-block;
            height: 24px;
            background: #ebeef7;
            line-height: 24px;
            border-radius: 6px;
            font-size: 12px;
            color: #4238c9;
            padding: 0 6px;
            margin-right: 4px;
            margin-bottom: 4px;

            .icon-close {
              width: 14px;
              height: 14px;
              background: #869add;
              border-radius: 4px;
              font-size: 8px;
              color: #fff;
              padding-left: 3px;
              margin-left: 6px;
              vertical-align: top;
              margin-top: 5px;

              &:hover {
                cursor: pointer;
              }
            }
          }
        }
      }

      .use-case-list {
        // height: 482px;
        height: 100%;
        padding: 10px 14px;
        padding-right: 0;
        padding-bottom: 16px;
      }
    }
  }
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 100%;

  .batch {
    line-height: 30px;
    display: flex;
    align-items: center;

    span {
      font-size: 14px;
    }

    .count {
      color: #2090a8;
    }

    .fix-btn {
      /* height: 24px; */
      line-height: 30px;
      width: 90px;
      display: flex;
      justify-content: center;
      align-items: center;

      .btn-text {
        margin-left: 6px;
      }

      &:hover {
        cursor: pointer;
        background: #fff;
        border-radius: 6px;
      }
    }
  }

  .close-icon {
    width: 9px;
    color: #999;

    &:hover {
      cursor: pointer;
      color: #4238c9;
    }
  }
`

const FixModal = styled(Modal)``
